/**
 * RecordLinksSection — blok "Powiązania rekordów" (F.RL), montowany przez
 * `contextRenderers['record_links_view']` na stronach detalu kontrahenta,
 * pracownika, zadania i wszystkich 7 klas dokumentów (patrz `RecursiveFormRenderer`
 * PLACEHOLDER_TYPES + `document-additional-renderers.tsx`).
 *
 * Self-persisting sub-resource (mirror `DocumentPartiesEditor`) — operuje na
 * ISTNIEJĄCYM rekordzie (`ownerId`), własny fetch/mutacje przez
 * `hooks/useRecordLinks.ts`, zero zależności od stanu formularza rodzica.
 *
 * Flow "Dodaj powiązanie": (1) wybierz typ powiązania (filtrowany do typów,
 * których `sourceTypes` dopuszczają `ownerType` — pusta lista = dowolny) →
 * (2) `RecordLinkTargetPickerModal` z `allowedTargetTypes` = `targetTypes`
 * wybranego typu (pusta lista rozwinięta tutaj do wszystkich typów) →
 * (3) opcjonalna notatka → zapis. Przycisk zablokowany w trybie "Wszystkie
 * firmy" (`companyId === null`) — backend wymaga JEDNEJ aktywnej firmy
 * (`COMPANY_CONTEXT_REQUIRED`).
 *
 * Nawigacja do drugiej strony powiązania: kontrahent/pracownik/towar/zadanie mają
 * stałe trasy (`/contractors/:id`, `/employees/:id`, `/products/:id`, `/tasks/:id`) — link
 * klikalny. Dla `otherType==='document'` backend zwraca dodatkowo `otherClass`
 * (`documents.class`) — trasa detalu zależy od klasy (faktura zakupu/sprzedaży,
 * umowa, pismo, dokument handlowy/wewnętrzny), więc mapujemy klasę → trasę
 * przez `DOCUMENT_CLASS_DETAIL_PATH` (mirror tras zarejestrowanych w
 * `TenantTabPanels.tsx`). Brak/nierozpoznana klasa → fallback na sam label
 * (plain text), nigdy złamany link.
 *
 * **Per-placement config** (`config` prop, opcjonalny) — settings JSONB
 * ustawione przez `RecordLinksViewElement.propertiesEditor` (right panel
 * buildera, patrz `RecordLinksElementConfigEditor.tsx`). Pozwala deployerowi
 * ograniczyć blok per konkretne umieszczenie w layoucie (bez wpływu na inne
 * placementy tego samego typu elementu):
 *  - `allowAdd`/`allowUnlink` — pokazują/ukrywają odpowiednio przycisk "Dodaj
 *    powiązanie" i akcję "Odłącz".
 *  - `allowedLinkTypeIds` — zawęża typy oferowane we flow dodawania (i grupy
 *    wyświetlane w liście — patrz filtr `groups` niżej).
 *  - `allowedTargetTypes` — przecinany z `targetTypes` wybranego typu
 *    powiązania przy budowaniu whitelisty dla pickera (nigdy nie ROZSZERZA
 *    whitelisty typu, tylko ją dodatkowo zawęża).
 *  - `defaultLinkTypeId` — preselekcja typu przy otwarciu flow dodawania
 *    (tylko gdy typ jest wśród dopuszczonych — inaczej ignorowany).
 *
 * `parseRecordLinksElementConfig` — defensywny parser `element.settings`
 * (JSONB, `unknown` na granicy) używany przez wszystkie 3 miejsca wiring
 * (`ContractorDetail`/`EmployeeDetail`/`document-additional-renderers.tsx`) —
 * jedno miejsce walidacji zamiast trzykrotnej duplikacji. Malformed/brakujące
 * pola są cicho ignorowane (fallback na `undefined` → domyślne zachowanie
 * "wszystko dozwolone", identyczne z zachowaniem sprzed tego increment'u).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import type {
  DocumentClass,
  RecordLinkOwnerType,
  RecordLinkSummary,
  RecordLinkTypeDTO,
} from '@saas/shared';
import { Link as LinkIcon } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import {
  type RecordLinkGroupItem,
  useRecordLinkMutations,
  useRecordLinks,
  useRecordLinkTypes,
} from '@/hooks/useRecordLinks';
import { taskPriorityLabel, taskStatusLabel } from '@/lib/core-field-option-labels';
import { resolveIcon } from '@/lib/icon-map';

import {
  type RecordLinkPickTarget,
  RecordLinkTargetPickerModal,
} from './RecordLinkTargetPickerModal';

const ALL_OWNER_TYPES: RecordLinkOwnerType[] = [
  'contractor',
  'employee',
  'product',
  'document',
  'task',
];

const OWNER_TYPE_DETAIL_PATH: Partial<Record<RecordLinkOwnerType, (id: string) => string>> = {
  contractor: (id) => `/contractors/${id}`,
  employee: (id) => `/employees/${id}`,
  product: (id) => `/products/${id}`,
  task: (id) => `/tasks/${id}`,
};

/** Trasa detalu per klasa dokumentu — mirror rejestracji tras w `TenantTabPanels.tsx`. */
const DOCUMENT_CLASS_DETAIL_PATH: Record<DocumentClass, (id: string) => string> = {
  purchase_invoice: (id) => `/purchase-invoices/${id}`,
  sale_invoice: (id) => `/sale-invoices/${id}`,
  contract: (id) => `/contracts/${id}`,
  letter: (id) => `/letters/${id}`,
  commercial: (id) => `/commercial-docs/${id}`,
  internal: (id) => `/internal-docs/${id}`,
  // Klasa `specjalne` — trasa `/special-docs/:id` zarejestrowana w TenantTabPanels.tsx.
  specjalne: (id) => `/special-docs/${id}`,
};

/** Type guard — `otherClass` z API jest surowym `string | null`, walidujemy przed lookupem. */
function isDocumentClass(value: string): value is DocumentClass {
  return Object.prototype.hasOwnProperty.call(DOCUMENT_CLASS_DETAIL_PATH, value);
}

/** Trasa detalu drugiego końca (dokument → per klasa, reszta → stała trasa). `null` gdy
 *  klasa dokumentu nierozpoznana/zredagowana → panel podglądu nie pokazuje „Otwórz". */
function buildDetailPath(item: RecordLinkGroupItem): string | null {
  if (item.otherType === 'document') {
    return item.otherClass && isDocumentClass(item.otherClass)
      ? DOCUMENT_CLASS_DETAIL_PATH[item.otherClass](item.otherId)
      : null;
  }
  const builder = OWNER_TYPE_DETAIL_PATH[item.otherType];
  return builder ? builder(item.otherId) : null;
}

/** `YYYY-MM-DD` → `DD.MM.YYYY` bez konstruowania `Date` (unika przesunięć strefy czasowej). */
function formatSummaryDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Znacznik czasu ISO → data i godzina pl-PL; fallback na surową wartość, gdy nieczytelna. */
function formatSummaryDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pl-PL');
}

/** Kwota (string z API) + waluta → format pl-PL; fallback gdy wartość nie jest liczbą. */
function formatSummaryAmount(amount: string | null, currency: string | null): string {
  if (!amount) return '—';
  const value = Number(amount);
  if (Number.isNaN(value)) return currency ? `${amount} ${currency}` : amount;
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency ?? 'PLN',
  }).format(value);
}

/** Per-placement config — patrz docstring nagłówkowy pliku „Per-placement config”. */
export interface RecordLinksElementConfig {
  allowAdd?: boolean;
  allowUnlink?: boolean;
  allowedLinkTypeIds?: string[];
  allowedTargetTypes?: RecordLinkOwnerType[];
  defaultLinkTypeId?: string | null;
}

/**
 * Defensywny parser `element.settings` (JSONB, `unknown` na granicy) → `RecordLinksElementConfig`.
 * Pola o złym typie/nieznanej wartości są cicho pomijane (fallback `undefined`) zamiast rzucać —
 * malformed settings NIE mogą wysadzić renderu bloku. Współdzielony przez 3 miejsca wiring
 * (`ContractorDetail`/`EmployeeDetail`/`document-additional-renderers.tsx`).
 */
export function parseRecordLinksElementConfig(raw: unknown): RecordLinksElementConfig {
  if (!raw || typeof raw !== 'object') return {};
  const settings = raw as Record<string, unknown>;
  const config: RecordLinksElementConfig = {};

  if (typeof settings.allowAdd === 'boolean') config.allowAdd = settings.allowAdd;
  if (typeof settings.allowUnlink === 'boolean') config.allowUnlink = settings.allowUnlink;

  if (Array.isArray(settings.allowedLinkTypeIds)) {
    config.allowedLinkTypeIds = settings.allowedLinkTypeIds.filter(
      (v): v is string => typeof v === 'string',
    );
  }

  if (Array.isArray(settings.allowedTargetTypes)) {
    config.allowedTargetTypes = settings.allowedTargetTypes.filter((v): v is RecordLinkOwnerType =>
      (ALL_OWNER_TYPES as string[]).includes(v as string),
    );
  }

  if (settings.defaultLinkTypeId === null || typeof settings.defaultLinkTypeId === 'string') {
    config.defaultLinkTypeId = settings.defaultLinkTypeId;
  }

  return config;
}

export interface RecordLinksSectionProps {
  ownerType: RecordLinkOwnerType;
  ownerId: string;
  /** Aktywna firma (kontekst zapisu) — `null` w trybie "Wszystkie firmy" blokuje dodawanie. */
  companyId: string | null;
  /** Konfiguracja per umieszczenie w layoucie (builder) — patrz docstring nagłówkowy pliku. */
  config?: RecordLinksElementConfig;
}

export function RecordLinksSection({
  ownerType,
  ownerId,
  companyId,
  config,
}: RecordLinksSectionProps) {
  const { t } = useTranslation();

  const groupsQuery = useRecordLinks(ownerType, ownerId, companyId);
  const linkTypesQuery = useRecordLinkTypes();
  const { createLink, updateNote, deleteLink } = useRecordLinkMutations(companyId);

  const allowAdd = config?.allowAdd !== false;
  const allowUnlink = config?.allowUnlink !== false;
  const allowedLinkTypeIds = config?.allowedLinkTypeIds ?? [];
  const configAllowedTargetTypes = config?.allowedTargetTypes ?? [];
  const defaultLinkTypeId = config?.defaultLinkTypeId ?? null;

  const [isAdding, setIsAdding] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<RecordLinkPickTarget | null>(null);
  const [note, setNote] = useState('');

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');

  const [pendingDelete, setPendingDelete] = useState<{ id: string; label: string } | null>(null);

  // Pozycja wybrana do podglądu w panelu obok listy. Wskaźnik do już załadowanej listy —
  // klik NIE dopobiera danych (summary przychodzi z listy). `null` = nic nie wybrano.
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  // Fetch typów powiązań padł — bez tego dropdown "Dodaj powiązanie" cicho pokazywałby
  // pustą listę (nieodróżnialną od "brak skonfigurowanych typów"), myląc usera. Blokujemy
  // cały flow dodawania z hintem zamiast pozwalać otworzyć formularz bez opcji do wyboru.
  const linkTypesLoadError = linkTypesQuery.isError;

  const availableLinkTypes: RecordLinkTypeDTO[] = (linkTypesQuery.data ?? [])
    .filter((lt) => lt.sourceTypes.length === 0 || lt.sourceTypes.includes(ownerType))
    .filter((lt) => allowedLinkTypeIds.length === 0 || allowedLinkTypeIds.includes(lt.id));
  const selectedType = availableLinkTypes.find((lt) => lt.id === selectedTypeId) ?? null;
  // `targetTypes` jest `string[]` w `RecordLinkTypeDTO` (kolumna jsonb bez typu na poziomie
  // DTO) — backend już waliduje wartości przez zod enum przy zapisie (mirror `toDTO` w
  // `record-link-types/route.ts`), więc cast tutaj jest bezpieczny.
  const typeTargetTypes: RecordLinkOwnerType[] = selectedType
    ? selectedType.targetTypes.length > 0
      ? (selectedType.targetTypes as RecordLinkOwnerType[])
      : ALL_OWNER_TYPES
    : [];
  // Whitelist skonfigurowana per placement TYLKO zawęża — nigdy nie rozszerza typów, które
  // sam typ powiązania już dopuszcza (przecięcie, nie unia).
  const allowedTargetTypes: RecordLinkOwnerType[] =
    selectedType && configAllowedTargetTypes.length > 0
      ? typeTargetTypes.filter((ownerTypeOption) =>
          configAllowedTargetTypes.includes(ownerTypeOption),
        )
      : typeTargetTypes;

  // Grupy widoczne w liście — zawężone do dopuszczonych typów, gdy placement ma whitelistę
  // (`allowedLinkTypeIds` niepusta). Domyślnie (`[]` = wszystkie) brak filtrowania.
  const visibleGroups = (groupsQuery.data ?? []).filter(
    (g) => allowedLinkTypeIds.length === 0 || allowedLinkTypeIds.includes(g.linkTypeId),
  );

  // Wybrana pozycja rozwiązywana z już załadowanych grup — gdy zniknie (odłączona/przefiltrowana),
  // `find` zwraca `undefined` → panel wraca do stanu pustego bez osobnego resetu.
  const selectedLink =
    selectedLinkId != null
      ? (visibleGroups.flatMap((g) => g.items).find((i) => i.id === selectedLinkId) ?? null)
      : null;

  function resetAddFlow() {
    setSelectedTypeId('');
    setPendingTarget(null);
    setNote('');
  }

  function handleOpenAdd() {
    if (companyId === null || !allowAdd || linkTypesLoadError) return;
    setSelectedTypeId(
      defaultLinkTypeId && availableLinkTypes.some((lt) => lt.id === defaultLinkTypeId)
        ? defaultLinkTypeId
        : '',
    );
    setIsAdding(true);
  }

  function handleCancelAdd() {
    resetAddFlow();
    setIsAdding(false);
  }

  function handleSubmitAdd() {
    if (!selectedType || !pendingTarget) return;
    createLink.mutate(
      {
        sourceType: ownerType,
        sourceId: ownerId,
        targetType: pendingTarget.type,
        targetId: pendingTarget.id,
        linkTypeId: selectedType.id,
        note: note.trim() ? note.trim() : null,
      },
      {
        onSuccess: () => {
          resetAddFlow();
          setIsAdding(false);
        },
      },
    );
  }

  function handleSaveNote(itemId: string) {
    updateNote.mutate(
      { id: itemId, note: editingNoteValue.trim() ? editingNoteValue.trim() : null },
      { onSuccess: () => setEditingItemId(null) },
    );
  }

  function handleConfirmDelete() {
    if (!pendingDelete) return;
    deleteLink.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: () => setPendingDelete(null),
    });
  }

  // Klik nazwy = wybór pozycji do podglądu w panelu obok (NIE nawigacja — nawigację przejmuje
  // „Otwórz" w panelu). Nazwa jest interaktywna przez `role="button"`, nie `<button>`, żeby nie
  // dokładać nowego surowego elementu (bramka biblioteki UI, §4.12); layout wiersza bez zmian.
  function renderOtherRecord(item: RecordLinkGroupItem) {
    const label = item.otherLabel ?? `${item.otherId.slice(0, 8)}…`;
    const isSelected = selectedLinkId === item.id;
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => setSelectedLinkId(item.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedLinkId(item.id);
          }
        }}
        aria-pressed={isSelected}
        className={`cursor-pointer text-sm font-medium hover:underline ${
          isSelected ? 'text-accent-700 underline' : 'text-accent-600'
        }`}
        data-testid={`record-link-item-${item.id}-target`}
      >
        {label}
      </span>
    );
  }

  function renderSummaryField(labelKey: string, value: string) {
    return (
      <div key={labelKey} className="flex items-baseline justify-between gap-3">
        <dt className="flex-shrink-0 text-xs text-text-muted">
          {t(`tenant.recordLinks.preview.fields.${labelKey}`)}
        </dt>
        <dd className="min-w-0 truncate text-right text-xs font-medium text-text-primary">
          {value}
        </dd>
      </div>
    );
  }

  function renderSummaryFields(summary: RecordLinkSummary) {
    switch (summary.kind) {
      case 'contractor':
        return [
          renderSummaryField('taxId', summary.taxId ?? '—'),
          renderSummaryField('email', summary.email ?? '—'),
          renderSummaryField('phone', summary.phone ?? '—'),
        ];
      case 'employee':
        return [
          renderSummaryField('position', summary.position ?? '—'),
          renderSummaryField('workEmail', summary.workEmail ?? '—'),
          renderSummaryField('phone', summary.phone ?? '—'),
        ];
      case 'product':
        return [
          renderSummaryField('code', summary.code ?? '—'),
          renderSummaryField('unit', summary.unit ?? '—'),
          renderSummaryField(
            'basePrice',
            formatSummaryAmount(summary.basePrice, summary.currencyCode),
          ),
        ];
      case 'document':
        return [
          renderSummaryField('systemNumber', summary.systemNumber ?? '—'),
          renderSummaryField('documentDate', formatSummaryDate(summary.documentDate)),
          renderSummaryField(
            'amountGross',
            formatSummaryAmount(summary.amountGross, summary.currencyCode),
          ),
          renderSummaryField('counterpartyName', summary.counterpartyName ?? '—'),
        ];
      case 'task':
        return [
          renderSummaryField('systemNumber', summary.systemNumber ?? '—'),
          renderSummaryField('status', taskStatusLabel(summary.status, t)),
          renderSummaryField('priority', taskPriorityLabel(summary.priority, t)),
          renderSummaryField('dueAt', formatSummaryDateTime(summary.dueAt)),
        ];
      default: {
        const _exhaustive: never = summary;
        return _exhaustive;
      }
    }
  }

  function renderPreview() {
    if (!selectedLink) {
      return (
        <p
          className="rounded-lg border border-dashed border-border-secondary p-4 text-center text-sm text-text-muted"
          data-testid="record-links-preview-empty"
        >
          {t('tenant.recordLinks.preview.empty')}
        </p>
      );
    }

    // `summary === null` ⟺ drugi koniec zredagowany (brak dostępu) — tylko komunikat,
    // bez pól i bez „Otwórz" (nawigacja i tak skończyłaby się odmową).
    if (!selectedLink.summary) {
      return (
        <div
          className="rounded-lg border border-border-primary bg-bg-primary p-3"
          data-testid={`record-links-preview-${selectedLink.id}`}
        >
          <p className="text-xs uppercase tracking-wide text-text-muted">
            {t('tenant.recordLinks.preview.title')}
          </p>
          <p className="mt-1 text-sm text-text-muted" data-testid="record-links-preview-no-access">
            {t('tenant.recordLinks.preview.noAccess')}
          </p>
        </div>
      );
    }

    const detailPath = buildDetailPath(selectedLink);
    return (
      <div
        className="space-y-3 rounded-lg border border-border-primary bg-bg-primary p-3"
        data-testid={`record-links-preview-${selectedLink.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-text-muted">
              {t('tenant.recordLinks.preview.title')}
            </p>
            <p className="truncate text-sm font-semibold text-text-primary">
              {selectedLink.otherLabel ?? `${selectedLink.otherId.slice(0, 8)}…`}
            </p>
          </div>
          {detailPath && (
            <RouterLink
              to={detailPath}
              className="flex-shrink-0 text-xs text-accent-600 hover:underline"
              data-testid="record-links-preview-open"
            >
              {t('tenant.recordLinks.preview.open')}
            </RouterLink>
          )}
        </div>

        <dl className="space-y-1.5" data-testid="record-links-preview-fields">
          {renderSummaryFields(selectedLink.summary)}
        </dl>

        {selectedLink.note && (
          <div>
            <p className="text-xs text-text-muted">{t('tenant.recordLinks.fields.note')}</p>
            <p className="whitespace-pre-wrap text-sm text-text-primary">{selectedLink.note}</p>
          </div>
        )}
      </div>
    );
  }

  if (groupsQuery.isLoading) {
    return (
      <div className="animate-pulse space-y-2" data-testid="record-links-loading" aria-busy="true">
        <div className="h-10 rounded bg-bg-secondary" />
        <div className="h-10 rounded bg-bg-secondary" />
      </div>
    );
  }

  if (groupsQuery.isError) {
    return (
      <div
        className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700"
        data-testid="record-links-error"
      >
        {t('tenant.recordLinks.loadError')}
      </div>
    );
  }

  const groups = visibleGroups;

  return (
    <section data-testid="record-links-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">{t('tenant.recordLinks.title')}</h3>
        {!isAdding && allowAdd && (
          <button
            type="button"
            onClick={handleOpenAdd}
            disabled={companyId === null || linkTypesLoadError}
            title={
              companyId === null
                ? t('tenant.recordLinks.addDisabledHint')
                : linkTypesLoadError
                  ? t('tenant.recordLinks.typesLoadError')
                  : undefined
            }
            className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="record-links-add-open-btn"
          >
            {t('tenant.recordLinks.addButton')}
          </button>
        )}
      </div>

      {groups.length === 0 ? (
        !isAdding && (
          <p className="text-sm text-text-muted" data-testid="record-links-empty">
            {t('tenant.recordLinks.empty')}
          </p>
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-3">
            {groups.map((group) => {
              const GroupIcon = resolveIcon(group.icon, LinkIcon);
              return (
                <div
                  key={`${group.linkTypeId}_${group.direction}`}
                  className="rounded-lg border border-border-primary bg-bg-primary p-3"
                  data-testid={`record-link-group-${group.linkTypeId}-${group.direction}`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span
                      className="flex h-5 w-5 items-center justify-center rounded-full"
                      style={{ backgroundColor: group.color ?? '#94a3b8' }}
                    >
                      <GroupIcon className="h-3 w-3 text-white" aria-hidden="true" />
                    </span>
                    <span className="text-sm font-medium text-text-primary">{group.label}</span>
                    <span className="text-xs text-text-muted">
                      {group.direction === 'outgoing'
                        ? t('tenant.recordLinks.directionOutgoing')
                        : t('tenant.recordLinks.directionIncoming')}
                    </span>
                  </div>
                  <ul
                    className="space-y-1.5"
                    data-testid={`record-link-group-${group.linkTypeId}-${group.direction}-items`}
                  >
                    {group.items.map((item) => (
                      <li
                        key={item.id}
                        className={`rounded border bg-bg-secondary p-2 ${
                          selectedLinkId === item.id
                            ? 'border-accent-400 ring-1 ring-accent-400'
                            : 'border-border-secondary'
                        }`}
                        data-testid={`record-link-item-${item.id}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1 space-y-0.5">
                            {renderOtherRecord(item)}
                            {item.note && editingItemId !== item.id && (
                              <p
                                className="text-xs text-text-muted"
                                data-testid={`record-link-item-${item.id}-note`}
                              >
                                {item.note}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingItemId(editingItemId === item.id ? null : item.id);
                                setEditingNoteValue(item.note ?? '');
                              }}
                              className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-bg-tertiary"
                              data-testid={`record-link-item-${item.id}-edit-note-btn`}
                            >
                              {t('tenant.recordLinks.editNote')}
                            </button>
                            {allowUnlink && (
                              <button
                                type="button"
                                onClick={() =>
                                  setPendingDelete({
                                    id: item.id,
                                    label: item.otherLabel ?? item.otherId,
                                  })
                                }
                                className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                                data-testid={`record-link-item-${item.id}-delete-btn`}
                              >
                                {t('tenant.recordLinks.disconnect')}
                              </button>
                            )}
                          </div>
                        </div>

                        {editingItemId === item.id && (
                          <div className="mt-2 space-y-2">
                            <textarea
                              value={editingNoteValue}
                              onChange={(e) => setEditingNoteValue(e.target.value)}
                              rows={2}
                              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                              data-testid={`record-link-item-${item.id}-note-input`}
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setEditingItemId(null)}
                                className="rounded-lg border border-border-secondary px-3 py-1 text-xs text-text-primary hover:bg-bg-secondary"
                              >
                                {t('common.cancel')}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSaveNote(item.id)}
                                disabled={updateNote.isPending}
                                className="rounded-lg bg-accent-600 px-3 py-1 text-xs text-white hover:bg-accent-700 disabled:opacity-60"
                                data-testid={`record-link-item-${item.id}-save-note-btn`}
                              >
                                {t('common.save')}
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <aside className="lg:sticky lg:top-4 lg:self-start" data-testid="record-links-preview">
            {renderPreview()}
          </aside>
        </div>
      )}

      {isAdding && (
        <div
          className="space-y-3 rounded-lg border border-border-primary bg-bg-secondary p-4"
          data-testid="record-links-add-form"
        >
          <p className="text-sm font-semibold text-text-primary">
            {t('tenant.recordLinks.addTitle')}
          </p>

          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              {t('tenant.recordLinks.fields.linkType')}
            </label>
            <Dropdown
              value={selectedTypeId}
              onChange={(value) => {
                setSelectedTypeId(value);
                setPendingTarget(null);
              }}
              options={[
                { value: '', label: t('tenant.recordLinks.fields.linkTypePlaceholder') },
                ...availableLinkTypes.map((lt) => ({ value: lt.id, label: lt.forwardLabel })),
              ]}
              data-testid="record-links-add-type-select"
            />
            {linkTypesLoadError && (
              <p className="mt-1 text-xs text-red-600" data-testid="record-links-add-types-error">
                {t('tenant.recordLinks.typesLoadError')}
              </p>
            )}
          </div>

          {selectedType && (
            <div>
              <label className="mb-1 block text-xs font-medium text-text-secondary">
                {t('tenant.recordLinks.fields.target')}
              </label>
              {pendingTarget ? (
                <div className="flex items-center justify-between rounded-md border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm">
                  <span
                    className="truncate text-text-primary"
                    data-testid="record-links-add-target-label"
                  >
                    {pendingTarget.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="ml-2 flex-shrink-0 text-xs text-accent-600 hover:underline"
                    data-testid="record-links-add-target-change-btn"
                  >
                    {t('tenant.recordLinks.fields.changeTarget')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-tertiary"
                  data-testid="record-links-add-target-pick-btn"
                >
                  {t('tenant.recordLinks.fields.pickTarget')}
                </button>
              )}
            </div>
          )}

          {selectedType && pendingTarget && (
            <div>
              <label className="mb-1 block text-xs font-medium text-text-secondary">
                {t('tenant.recordLinks.fields.note')}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                data-testid="record-links-add-note-input"
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancelAdd}
              className="rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmitAdd}
              disabled={!selectedType || !pendingTarget || createLink.isPending}
              className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="record-links-add-submit-btn"
            >
              {createLink.isPending ? t('common.saving') : t('tenant.recordLinks.addButton')}
            </button>
          </div>
        </div>
      )}

      <RecordLinkTargetPickerModal
        open={pickerOpen}
        allowedTargetTypes={allowedTargetTypes}
        companyId={companyId}
        onClose={() => setPickerOpen(false)}
        onPick={(target) => setPendingTarget(target)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('tenant.recordLinks.deleteConfirmTitle')}
        message={
          pendingDelete
            ? t('tenant.recordLinks.deleteConfirmBody', { identity: pendingDelete.label })
            : ''
        }
        confirmLabel={t('tenant.recordLinks.disconnect')}
        confirming={deleteLink.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </section>
  );
}
